import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Mail, Clock, Shield, LifeBuoy, Handshake, CreditCard, Newspaper, AlertTriangle } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import LocaleToggle from "@/components/LocaleToggle";
import { useLocale } from "@/hooks/useLocale";

interface Lane {
  icon: React.ComponentType<{ className?: string }>;
  title: { en: string; es: string };
  address: string;
  sla: { en: string; es: string };
  description: { en: string; es: string };
  examples: { en: string; es: string }[];
}

const LANES: Lane[] = [
  {
    icon: LifeBuoy,
    title: { en: "General support", es: "Soporte general" },
    address: "info@emrkt.co",
    sla: { en: "≤ 2 business days", es: "≤ 2 días hábiles" },
    description: {
      en: "Account questions, how-tos, feature feedback, and anything you are not sure where to send.",
      es: "Preguntas de cuenta, guías de uso, comentarios sobre funciones y cualquier duda sobre a quién escribir.",
    },
    examples: [
      { en: "Login trouble", es: "Problemas de inicio de sesión" },
      { en: "How a feature works", es: "Cómo funciona una función" },
      { en: "Data question", es: "Pregunta sobre datos" },
      { en: "General feedback", es: "Comentarios generales" },
    ],
  },
  {
    icon: Handshake,
    title: { en: "Partners & affiliates", es: "Aliados y afiliados" },
    address: "info@emrkt.co (subject: Partner)",
    sla: { en: "≤ 3 business days", es: "≤ 3 días hábiles" },
    description: {
      en: "Applications, MOU questions, onboarding, referrals, and playbook questions.",
      es: "Solicitudes, preguntas sobre el MOU, incorporación, referidos y dudas del playbook.",
    },
    examples: [
      { en: "Apply as Advocate/Partner/Anchor", es: "Solicitar como Advocate/Partner/Anchor" },
      { en: "MOU or payout questions", es: "Preguntas sobre MOU o pagos" },
      { en: "Referral link help", es: "Ayuda con enlaces de referido" },
    ],
  },
  {
    icon: CreditCard,
    title: { en: "Billing & subscriptions", es: "Pagos y suscripciones" },
    address: "info@emrkt.co (subject: Billing)",
    sla: { en: "≤ 2 business days", es: "≤ 2 días hábiles" },
    description: {
      en: "Invoices, W-9s, plan changes, refunds, and receipt questions.",
      es: "Facturas, formularios W-9, cambios de plan, reembolsos y preguntas sobre recibos.",
    },
    examples: [
      { en: "Change plan", es: "Cambiar de plan" },
      { en: "Cancel subscription", es: "Cancelar la suscripción" },
      { en: "Invoice request", es: "Solicitar factura" },
      { en: "Refund inquiry", es: "Consulta de reembolso" },
    ],
  },
  {
    icon: Newspaper,
    title: { en: "Press & partnerships", es: "Prensa y alianzas" },
    address: "info@emrkt.co (subject: Press)",
    sla: { en: "≤ 5 business days", es: "≤ 5 días hábiles" },
    description: {
      en: "Media requests, interviews, speaking, sponsorship, and demos for institutions.",
      es: "Solicitudes de medios, entrevistas, charlas, patrocinios y demos para instituciones.",
    },
    examples: [
      { en: "Interview request", es: "Solicitud de entrevista" },
      { en: "Sponsorship inquiry", es: "Consulta de patrocinio" },
      { en: "Demo for funders or agencies", es: "Demo para financiadores o agencias" },
    ],
  },
  {
    icon: Shield,
    title: { en: "Security & privacy", es: "Seguridad y privacidad" },
    address: "info@emrkt.co (subject: Security)",
    sla: { en: "Acknowledged within 24h", es: "Confirmamos en 24 horas" },
    description: {
      en: "Vulnerability reports, data-access requests, deletion requests, and privacy questions. See /security for our disclosure commitments.",
      es: "Reportes de vulnerabilidades, solicitudes de acceso o eliminación de datos y preguntas de privacidad. Vea /security para nuestros compromisos de divulgación.",
    },
    examples: [
      { en: "Report a vulnerability", es: "Reportar una vulnerabilidad" },
      { en: "Data access / deletion request", es: "Solicitud de acceso o eliminación" },
      { en: "Privacy question", es: "Pregunta de privacidad" },
    ],
  },
  {
    icon: AlertTriangle,
    title: { en: "Trust & safety", es: "Confianza y seguridad" },
    address: "info@emrkt.co (subject: Report)",
    sla: { en: "Triage within 1 business day", es: "Revisamos en 1 día hábil" },
    description: {
      en: "Report abuse, harassment, spam, or content that breaks our Acceptable Use policy.",
      es: "Reporte abuso, acoso, spam o contenido que viole nuestra política de uso aceptable.",
    },
    examples: [
      { en: "Report a user or post", es: "Reportar un usuario o publicación" },
      { en: "Flag misinformation", es: "Marcar desinformación" },
      { en: "Copyright / DMCA", es: "Derechos de autor / DMCA" },
    ],
  },
];

const T = {
  eyebrow: { en: "Contact", es: "Contacto" },
  h1: { en: "One inbox. Clear response times.", es: "Un solo buzón. Tiempos de respuesta claros." },
  intro: {
    en: <>Every message reaches the same shared inbox — </>,
    es: <>Cada mensaje llega al mismo buzón compartido — </>,
  },
  introMid: {
    en: <>. We route by subject and reply within the times below. For a faster answer, check the </>,
    es: <>. Ordenamos por asunto y respondemos dentro de los plazos indicados. Para una respuesta más rápida, revise el </>,
  },
  helpCenter: { en: "Help Center", es: "Centro de ayuda" },
  introEnd: {
    en: <> first — most common questions are answered there.</>,
    es: <> primero — allí están las respuestas más comunes.</>,
  },
  hours: { en: "Business hours", es: "Horario de atención" },
  hoursVal: { en: "Mon–Fri, 9am–5pm CT", es: "Lun a vie, 9am a 5pm CT" },
  hoursNote: { en: "Excluding US federal holidays.", es: "Excluye feriados federales de EE. UU." },
  inbox: { en: "Shared inbox", es: "Buzón compartido" },
  inboxNote: { en: "Please include a clear subject prefix.", es: "Por favor incluya un asunto claro." },
  urgent: { en: "Urgent security", es: "Seguridad urgente" },
  urgentVal: { en: "Acknowledged within 24h", es: "Confirmamos en 24 horas" },
  urgentSee: { en: "See", es: "Vea" },
  urgentTail: { en: "for disclosure.", es: "para el proceso de divulgación." },
  lanesAria: { en: "Response lanes", es: "Canales de respuesta" },
  emailLabel: { en: "Email:", es: "Correo:" },
  handleH2: { en: "How we handle your message", es: "Cómo tratamos su mensaje" },
  steps: {
    en: [
      ["Auto-acknowledged", " — every email gets an instant receipt so you know we have it."],
      ["Triaged next business day", " — a real person reads it, tags the lane, and assigns an owner."],
      ["First reply within the response time", " — see the lane above. If we need more time, we will tell you why and when."],
      ["Closed with a summary", " — we recap what changed or what to expect next, so nothing gets lost."],
    ],
    es: [
      ["Confirmación automática", " — cada correo recibe un aviso inmediato para que sepa que llegó."],
      ["Clasificación el próximo día hábil", " — una persona real lo lee, lo etiqueta y le asigna responsable."],
      ["Primera respuesta dentro del plazo", " — vea el canal arriba. Si necesitamos más tiempo, le diremos por qué y cuándo."],
      ["Cierre con resumen", " — resumimos lo que cambió o los próximos pasos para que nada se pierda."],
    ],
  },
  slaFine: {
    en: "Response times are targets, not guarantees. Weekends and US federal holidays do not count toward business-day windows. Complex issues (legal, engineering, third-party) may take longer — we will keep you posted.",
    es: "Los tiempos son metas, no garantías. Los fines de semana y feriados federales de EE. UU. no cuentan como días hábiles. Los casos complejos (legales, técnicos, con terceros) pueden tardar más — le mantendremos al tanto.",
  },
  beforeH: { en: "Before you write", es: "Antes de escribir" },
  beforeSearch: { en: "Search the ", es: "Busque en el " },
  beforeStatus: { en: "Check ", es: "Revise el " },
  beforeStatusLink: { en: "system status", es: "estado del sistema" },
  beforeStatusTail: { en: " for outages", es: " para ver interrupciones" },
  beforeHandbook: { en: "Review the ", es: "Revise el " },
  beforeHandbookLink: { en: "Partner Handbook", es: "Partner Handbook" },
  beforeHandbookTail: { en: " for program questions", es: " para preguntas del programa" },
  helpUsH: { en: "Help us help you", es: "Ayúdenos a ayudarle" },
  helpUs: {
    en: [
      "Use a clear subject line (see prefixes above)",
      "Include your account email and, if relevant, the page URL",
      "Attach screenshots for bugs or unexpected behavior",
      "Never send passwords, MFA codes, or full card numbers",
    ],
    es: [
      "Use un asunto claro (vea los prefijos arriba)",
      "Incluya el correo de su cuenta y, si aplica, el enlace de la página",
      "Adjunte capturas de pantalla para errores o comportamientos inesperados",
      "Nunca envíe contraseñas, códigos MFA ni números de tarjeta completos",
    ],
  },
  updated: { en: "Last updated July 13, 2026", es: "Última actualización: 13 de julio de 2026" },
};

const Contact = () => {
  const { locale } = useLocale();

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "The Emerging Market",
    url: "https://emrkt.co",
    email: "info@emrkt.co",
    contactPoint: LANES.map((l) => ({
      "@type": "ContactPoint",
      contactType: l.title.en,
      email: "info@emrkt.co",
      availableLanguage: ["English", "Spanish"],
    })),
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Contact & Response Times — The Emerging Market</title>
        <meta
          name="description"
          content="Reach The Emerging Market at info@emrkt.co. Response-time commitments for support, partners, billing, press, security, and trust & safety."
        />
        <link rel="canonical" href="https://emrkt.co/contact" />
        <link rel="alternate" hrefLang="en" href="https://emrkt.co/contact" />
        <link rel="alternate" hrefLang="es" href="https://emrkt.co/contact" />
        <link rel="alternate" hrefLang="x-default" href="https://emrkt.co/contact" />
        <meta property="og:title" content="Contact & Response Times — The Emerging Market" />
        <meta
          property="og:description"
          content="One inbox, clear SLAs. How to reach us and when to expect a reply."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://emrkt.co/contact" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </Helmet>

      <SiteHeader />

      <main className="container mx-auto px-6 py-16 max-w-5xl">
        <header className="mb-10">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-semibold">
              <Mail className="h-4 w-4" />
              <span>{T.eyebrow[locale]}</span>
            </div>
            <LocaleToggle />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            {T.h1[locale]}
          </h1>
          <p className="text-muted-foreground text-lg max-w-3xl">
            {T.intro[locale]}
            <a href="mailto:info@emrkt.co" className="text-primary underline">
              info@emrkt.co
            </a>
            {T.introMid[locale]}
            <Link to="/help" className="text-primary underline">{T.helpCenter[locale]}</Link>
            {T.introEnd[locale]}
          </p>

          <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-3xl">
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                <Clock className="h-3.5 w-3.5" /> {T.hours[locale]}
              </div>
              <p className="text-sm text-foreground">{T.hoursVal[locale]}</p>
              <p className="text-xs text-muted-foreground mt-1">{T.hoursNote[locale]}</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                <Mail className="h-3.5 w-3.5" /> {T.inbox[locale]}
              </div>
              <a href="mailto:info@emrkt.co" className="text-sm text-primary underline break-all">
                info@emrkt.co
              </a>
              <p className="text-xs text-muted-foreground mt-1">{T.inboxNote[locale]}</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                <Shield className="h-3.5 w-3.5" /> {T.urgent[locale]}
              </div>
              <p className="text-sm text-foreground">{T.urgentVal[locale]}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {T.urgentSee[locale]} <Link to="/security" className="text-primary underline">/security</Link> {T.urgentTail[locale]}
              </p>
            </div>
          </div>
        </header>

        <section aria-label={T.lanesAria[locale]} className="grid md:grid-cols-2 gap-4">
          {LANES.map((lane) => {
            const Icon = lane.icon;
            return (
              <article
                key={lane.title.en}
                className="rounded-lg border border-border bg-card p-6 flex flex-col"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-primary/10 text-primary p-2">
                      <Icon className="h-4 w-4" />
                    </span>
                    <h2 className="font-display font-semibold text-lg text-foreground">
                      {lane.title[locale]}
                    </h2>
                  </div>
                  <span className="text-[11px] uppercase tracking-wider text-primary font-semibold whitespace-nowrap">
                    {lane.sla[locale]}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  {lane.description[locale]}
                </p>
                <ul className="text-xs text-muted-foreground mb-4 flex flex-wrap gap-1.5">
                  {lane.examples.map((ex) => (
                    <li
                      key={ex.en}
                      className="rounded-full border border-border px-2 py-0.5"
                    >
                      {ex[locale]}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto text-xs text-muted-foreground">
                  {T.emailLabel[locale]}{" "}
                  <a
                    href={`mailto:info@emrkt.co?subject=${encodeURIComponent(lane.title.en)}`}
                    className="text-primary underline"
                  >
                    {lane.address}
                  </a>
                </div>
              </article>
            );
          })}
        </section>

        <section className="mt-12 rounded-lg border border-border bg-card p-6">
          <h2 className="font-display font-semibold text-xl mb-3">{T.handleH2[locale]}</h2>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal pl-5 leading-relaxed">
            {T.steps[locale].map(([bold, rest]) => (
              <li key={bold}>
                <strong className="text-foreground">{bold}</strong>{rest}
              </li>
            ))}
          </ol>
          <p className="text-xs text-muted-foreground mt-4">{T.slaFine[locale]}</p>
        </section>

        <section className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="font-display font-semibold mb-2">{T.beforeH[locale]}</h3>
            <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-5">
              <li>
                {T.beforeSearch[locale]}<Link to="/help" className="text-primary underline">{T.helpCenter[locale]}</Link>
              </li>
              <li>
                {T.beforeStatus[locale]}<Link to="/status" className="text-primary underline">{T.beforeStatusLink[locale]}</Link>{T.beforeStatusTail[locale]}
              </li>
              <li>
                {T.beforeHandbook[locale]}<Link to="/partners/handbook" className="text-primary underline">{T.beforeHandbookLink[locale]}</Link>{T.beforeHandbookTail[locale]}
              </li>
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="font-display font-semibold mb-2">{T.helpUsH[locale]}</h3>
            <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-5">
              {T.helpUs[locale].map((s) => (<li key={s}>{s}</li>))}
            </ul>
          </div>
        </section>

        <p className="text-xs text-muted-foreground mt-10 text-center">{T.updated[locale]}</p>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Contact;